/** @jsx isolateComponent */
import { isolateComponent } from '@zapier/style-encapsulation';
import { Heading, HeadingLevel, Typography } from '@zapier/design-system';

import BackButton from '../../shared/BackButton/BackButton';
import NavDots from './NavDots';
import Feedback from './Feedback';
import { SizeType } from '../../../types';

type HelpBlockProps = {
  /** The content rendered inside of the help block. */
  children: React.ReactNode;
  /** The index of the highlighted dot. Starts at zero. */
  currentStepIndex: number;
  /** An array of tooltips displayed over the numbered dots. */
  dotTooltips: string[];
  /** Called when the 'back' button is clicked. */
  onGoBack: () => void;
  /** Called when the 'next' button is clicked.*/
  onGoForward: () => void;
  /** Called when a dot is clicked to go to a step. */
  onGoToStep: (index: number) => void;
  /** `true` if the feedback form should be displa*/
  showFeedback: boolean;
  /** The heading text displayed above the `children` content. */
  title: string;
  /** The size at which the content is displayed. */
  size?: SizeType;
};

const Styles = {
  backButton: {
    // Prevent the back button from stretching across the container.
    marginRight: 'auto',
  },
  inner: {
    display: 'grid',
    gridGap: 20,
    gridTemplateColumns: '1fr min-content',
  },
  root: {
    display: 'grid',
    gridGap: 20,
  },
  stepTitle: {
    ...Typography.SectionHeader,
  },
  rebrandStepTitle: {
    ...Typography.SectionHeader,
  },
};

/**
  Wraps the content for each step of the contact form.
  Displays a title and numbered dots that can be used to navigate to previous steps.
  Optionally displays a feedback form beneath the content of the step.
*/
const HelpBlock = ({
  children,
  currentStepIndex,
  dotTooltips,
  onGoBack,
  onGoForward,
  onGoToStep,
  showFeedback,
  title,
  size = 'large',
}: HelpBlockProps) => {
  const showDots = dotTooltips.length > 1;

  return (
    <div css={Styles.root}>
      <div css={Styles.backButton}>
        <BackButton onClick={onGoBack} />
      </div>
      <div css={Styles.inner}>
        <Heading css={Styles.rebrandStepTitle}>{title}</Heading>
        {showDots && (
          <NavDots
            activeDotIndex={currentStepIndex}
            onDotClick={onGoToStep}
            tooltips={dotTooltips}
          />
        )}
      </div>
      <HeadingLevel>{children}</HeadingLevel>
      {/* `key` makes `<Feedback>` remount and reset its state between stages */}
      {showFeedback && (
        <Feedback key={title} onGoToNextStep={onGoForward} size={size} />
      )}
    </div>
  );
};

export default HelpBlock;
